import React, { useRef, useEffect } from 'react';

const VERT = `#version 300 es
in vec2 position;
void main(){gl_Position=vec4(position,0,1);}`;

const FRAG = `#version 300 es
precision highp float;
out vec4 O;
uniform float T;
uniform vec2 R;

mat2 rot(float a){float c=cos(a),s=sin(a);return mat2(c,-s,s,c);}

// Solid tetrahedron SDF
float sdTetra(vec3 p,float s){
  float k=.57735*s;
  return max(max(dot(p,vec3(k,k,k)),dot(p,vec3(-k,-k,k))),
             max(dot(p,vec3(-k,k,-k)),dot(p,vec3(k,-k,-k))))-s;
}

// Edge distance — wireframe
float sdSeg(vec3 p,vec3 a,vec3 b){
  vec3 ab=b-a,ap=p-a;
  return length(ap-ab*clamp(dot(ap,ab)/dot(ab,ab),0.,1.));
}

float tetraWire(vec3 p,vec3 v0,vec3 v1,vec3 v2,vec3 v3){
  float d=sdSeg(p,v0,v1);
  d=min(d,sdSeg(p,v0,v2));
  d=min(d,sdSeg(p,v0,v3));
  d=min(d,sdSeg(p,v1,v2));
  d=min(d,sdSeg(p,v2,v3));
  d=min(d,sdSeg(p,v3,v1));
  return d;
}

void main(){
  vec2 uv=(gl_FragCoord.xy-.5*R)/R.y;
  float t=T*.15;

  // Tetrahedron size (25% shorter)
  float sz=0.975;

  // Vertices for wireframe
  vec3 v0=vec3(0.,sz,0.);
  vec3 v1=vec3(sz*.943,-sz*.333,0.);
  vec3 v2=vec3(-sz*.471,-sz*.333,sz*.816);
  vec3 v3=vec3(-sz*.471,-sz*.333,-sz*.816);

  // Camera
  vec3 ro=vec3(0.,.15,4.8);
  vec3 rd=normalize(vec3(uv,-1.7));

  // Gentle rotation on camera+scene
  mat2 rx=rot(t*.35);
  mat2 ry=rot(sin(t*.25)*.06);
  rd.xz*=rx;ro.xz*=rx;
  rd.yz*=ry;ro.yz*=ry;

  // Also rotate vertices for wireframe distance
  v0.xz*=rx;v0.yz*=ry;
  v1.xz*=rx;v1.yz*=ry;
  v2.xz*=rx;v2.yz*=ry;
  v3.xz*=rx;v3.yz*=ry;

  vec3 col=vec3(0.);
  float td=0.;
  float gAcc=0.;

  // Raymarch the solid tetrahedron with volumetric glow
  for(int i=0;i<80;i++){
    vec3 p=ro+rd*td;
    float d=sdTetra(p,sz);
    float ad=abs(d);
    float wd=tetraWire(p,v0,v1,v2,v3);

    // Accumulate volumetric glow near surface
    if(ad<2.){
      float surfGlow=exp(-ad*2.)*.018;
      float edgeGlow=exp(-wd*4.)*.03;

      // Warm amber/orange palette
      float h=dot(p,vec3(.3,.6,.2))*.5+t*.3;
      vec3 warmCol=mix(vec3(.9,.5,.06),vec3(1.,.75,.2),sin(h)*.5+.5);
      col+=warmCol*(surfGlow+edgeGlow);
      gAcc+=surfGlow+edgeGlow;
    }

    // Hit the solid surface
    if(d<.005){
      // Compute normal
      vec2 e=vec2(.002,0);
      vec3 n=normalize(vec3(
        sdTetra(p+e.xyy,sz)-sdTetra(p-e.xyy,sz),
        sdTetra(p+e.yxy,sz)-sdTetra(p-e.yxy,sz),
        sdTetra(p+e.yyx,sz)-sdTetra(p-e.yyx,sz)));

      // Fresnel — edges of the solid shape glow brighter
      float fr=pow(1.-abs(dot(n,-rd)),3.);

      // Edge proximity on surface
      float edgeFactor=exp(-wd*3.5);

      // Surface color — warm amber/gold
      float sh=dot(p,vec3(.4,.5,.3))*.8+t*.2;
      vec3 sc=mix(vec3(.7,.35,.03),vec3(1.,.65,.12),sin(sh)*.5+.5);

      // Translucent face + Fresnel edge glow + wireframe edge highlight
      col+=sc*(.08+fr*1.2+edgeFactor*.7);
      break;
    }

    td+=d*.7;
    if(td>18.)break;
  }

  // Subtle warm ambient glow
  col+=vec3(.08,.04,.01)*exp(-dot(uv,uv)*1.8)*.5;

  // Tone mapping
  col=col/(col+1.);
  col=pow(col,vec3(.9));

  O=vec4(col,1.);
}`;

const TetrahedronMonolith = ({ className }) => {
  const ref = useRef(null);
  const raf = useRef(null);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const gl = c.getContext('webgl2', { alpha: false, antialias: false });
    if (!gl) return;

    const mk = (type, src) => {
      const sh = gl.createShader(type);
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        console.error('Shader error:', gl.getShaderInfoLog(sh));
      }
      return sh;
    };

    const vs = mk(gl.VERTEX_SHADER, VERT);
    const fs = mk(gl.FRAGMENT_SHADER, FRAG);
    const pg = gl.createProgram();
    gl.attachShader(pg, vs);
    gl.attachShader(pg, fs);
    gl.linkProgram(pg);

    if (!gl.getProgramParameter(pg, gl.LINK_STATUS)) {
      console.error('Program error:', gl.getProgramInfoLog(pg));
      return;
    }

    gl.useProgram(pg);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const posLoc = gl.getAttribLocation(pg, 'position');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const uT = gl.getUniformLocation(pg, 'T');
    const uR = gl.getUniformLocation(pg, 'R');

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio, 1.5);
      c.width = c.clientWidth * dpr;
      c.height = c.clientHeight * dpr;
      gl.viewport(0, 0, c.width, c.height);
      gl.uniform2f(uR, c.width, c.height);
    };

    resize();
    window.addEventListener('resize', resize);

    const startTime = performance.now();
    const loop = () => {
      gl.uniform1f(uT, (performance.now() - startTime) * 0.001);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('resize', resize);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      className={className}
      style={{ width: '100%', height: '100%', display: 'block' }}
    />
  );
};

export default TetrahedronMonolith;
